import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../../../services/analyticsService';
import { buildFanSuggestions, type FanAngleKey, type FanSuggestionsParams } from '../../../services/fanSuggestionsService';
import {
  buildChipList,
  fanChipsFor,
  fanMealOf,
  fanShelf,
  FAN_CHIP_ACCENT,
  FAN_PAGE_SIZE,
  remainingFor,
  type FanChip,
  type FanChipId,
} from '../../../services/fanChips';
import type { RecipeIndexEntry } from '../../../services/unifiedRecipeService';
import { useToday } from '../../../hooks/useToday';

/** One row of the list, an angle of the mix or a line of a chip's list. */
export interface FanChoiceRow {
  recipe: RecipeIndexEntry;
  angleKey?: FanAngleKey;
  /** Stripe of a chip's row. The mix takes its colour from the angle. */
  accent?: string;
  reasonKey?: string;
  reasonParams?: Record<string, string | number>;
}

/** What the sheet and the drawer draw above the list. */
export interface FanChipBarProps {
  chips: FanChip[];
  picked: FanChipId | null;
  /** How many « 5 nouvelles » would bring: a page, or what is left of it. */
  moreCount: number;
  /** Nothing left and something was already asked for: start the list again. */
  canRestart: boolean;
  onMore: () => void;
  onToggle: (id: FanChipId) => void;
}

export type FanChoiceParams = Omit<FanSuggestionsParams<RecipeIndexEntry>, 'seed'> & { sk: number };

const MIX = 'mix';

interface Progress {
  key: number;
  chip: FanChipId | null;
  /** Per list, so letting go of a chip finds the page it left. */
  seen: Record<string, string[]>;
  pages: Record<string, number>;
}

const NOTHING_SEEN: string[] = [];

/**
 * The chips and the list of one opening of the « Remplacer » sheet (#864).
 * `openingKey` changes every time it opens (the hosts' fan seed), and what was
 * asked belongs to that opening only: the next dish starts on the mix.
 */
export function useFanChoice({ params, openingKey, surface }: {
  /** Null while the sheet is closed or choosing an accompaniment. */
  params: FanChoiceParams | null;
  openingKey: number;
  surface: 'mobile_sheet' | 'desktop_drawer';
}): { rows: FanChoiceRow[]; chipBar?: FanChipBarProps; trackPick: (recipeId: string) => void } {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const today = useToday();
  const [state, setState] = useState<Progress>({ key: openingKey, chip: null, seen: {}, pages: {} });
  const progress = useMemo<Progress>(
    () => (state.key === openingKey ? state : { key: openingKey, chip: null, seen: {}, pages: {} }),
    [state, openingKey],
  );

  const shelf = useMemo(() => (params ? fanShelf(params) : null), [params]);
  const chips = useMemo(() => (params && shelf ? fanChipsFor(shelf, params) : []), [params, shelf]);

  // A chip this slot does not offer is not held on to.
  const chip = chips.some((c) => c.id === progress.chip) ? progress.chip : null;
  const list = chip ?? MIX;
  const seen = progress.seen[list] ?? NOTHING_SEEN;
  const page = progress.pages[list] ?? 0;

  const rows = useMemo<FanChoiceRow[]>(() => {
    if (!params || !shelf) return [];
    const seed = openingKey + page * 7919;
    if (chip) {
      return buildChipList(chip, shelf, params, { seed, seen, today, language })
        .map((row) => ({ ...row, accent: FAN_CHIP_ACCENT[chip] }));
    }
    const shown = new Set(seen);
    return buildFanSuggestions({ ...params, seed, pool: shelf.day.filter((r) => !shown.has(r.id)) })
      .map((s) => ({ recipe: s.recipe, angleKey: s.angleKey, reasonKey: s.reasonKey, reasonParams: s.reasonParams }));
  }, [params, shelf, chip, seen, page, openingKey, today, language]);

  const remaining = useMemo(() => {
    if (!params || !shelf) return 0;
    return remainingFor(chip, shelf, params, [...seen, ...rows.map((r) => r.recipe.id)]);
  }, [params, shelf, chip, seen, rows]);

  const meal = params ? fanMealOf(params.sk) : null;

  const onMore = useCallback(() => {
    const restart = remaining === 0;
    trackEvent('fan_more_tapped', { chip: chip ?? 'mix', page, restart, meal, surface });
    setState({
      ...progress,
      chip,
      seen: { ...progress.seen, [list]: restart ? [] : [...seen, ...rows.map((r) => r.recipe.id)] },
      pages: { ...progress.pages, [list]: page + 1 },
    });
  }, [remaining, chip, list, page, meal, surface, progress, seen, rows]);

  const onToggle = useCallback((id: FanChipId) => {
    const next = chip === id ? null : id;
    trackEvent('fan_chip_toggled', { chip: id, on: next === id, meal, surface });
    setState({ ...progress, chip: next });
  }, [chip, meal, surface, progress]);

  const trackPick = useCallback((recipeId: string) => {
    const row = rows.find((r) => r.recipe.id === recipeId);
    trackEvent('fan_recipe_chosen', { chip: chip ?? 'mix', angle: row?.angleKey ?? null, page, meal, surface });
  }, [rows, chip, page, meal, surface]);

  const moreCount = Math.min(FAN_PAGE_SIZE, remaining);
  const canRestart = remaining === 0 && page > 0;
  const chipBar = params && (chips.length > 0 || moreCount > 0 || canRestart)
    ? { chips, picked: chip, moreCount, canRestart, onMore, onToggle }
    : undefined;

  return { rows, chipBar, trackPick };
}
